<script setup lang="ts">
import { StudioUser } from '@/modules/Studios/types'
import { ref } from 'vue'
import { useImage } from '@/shared/composables/image'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { getDateFormatted } from '@/services/date'

const props = withDefaults(
  defineProps<{
    member: StudioUser
    isExpandable?: boolean
  }>(),
  {
    isExpandable: true,
  }
)

const { getAvatar, getEventImg } = useImage()

const isExpanded = ref(!props.isExpandable)

const getRankText = (rank: number): string => {
  if (rank === 1) return '1st'
  if (rank === 2) return '2nd'
  if (rank === 3) return '3rd'
  if (rank === 4) return 'Top 4'
  if (rank === 5) return 'Top 5'
  return ''
}

const getRankIconClass = (rank: number): string => {
  if (rank === 1) return 'rank-icon icon-gold'
  if (rank === 2) return 'rank-icon icon-silver'
  if (rank === 3) return 'rank-icon icon-bronze'
  return 'rank-icon icon-compete'
}

const getEventDate = (date: string): string => {
  return getDateFormatted(new Date(date))
}
</script>

<template>
  <div
    class="studio-member-chip"
    :class="{ 'studio-member-chip--expanded': isExpanded }"
  >
    <button
      class="member-header"
      @click="isExpanded = !isExpanded"
    >
      <div class="member-header__left">
        <router-link
          :to="{ name: 'Profile', params: { id: String(member.id) } }"
          class="member-header__profile-link"
        >
          <v-img
            :src="getAvatar(member.avatar || '', 64)"
            :alt="member.dancerName"
            class="member-header__image"
          />
          <span class="member-header__title">
            {{ member.dancerName }}
          </span>
        </router-link>
      </div>
      <div class="member-header__right">
        <v-icon
          v-if="isExpandable"
          class="member-header__icon"
        >
          {{ isExpanded ? mdiChevronUp : mdiChevronDown }}
        </v-icon>
      </div>
    </button>
    <v-expand-transition>
      <div
        v-if="isExpanded"
        class="member-details"
      >
        <div class="member-details__events">
          <div v-if="!member.events?.length">
            <p class="text-center">This member has no events yet.</p>
          </div>
          <template v-else>
            <div
              v-for="event in member.events"
              :key="event.id"
              class="event-item"
            >
              <router-link
                :to="{ name: 'Event', params: { id: String(event.id) } }"
                class="event-header-link"
              >
                <div class="event-header">
                  <div class="event-header__left">
                    <v-img
                      :src="getEventImg(event.image || '', 64)"
                      :alt="event.title"
                      class="event-header__image"
                    />
                    <span class="event-header__title">
                      {{ event.title }}
                    </span>
                  </div>
                  <div class="event-header__right">
                    <span
                      v-if="event.city"
                      class="event-header__location"
                    >
                      {{ event.city }}{{ event.state ? `, ${event.state}` : '' }}
                    </span>
                    <span class="event-header__date">
                      {{ getEventDate(event.dateStart) }}
                    </span>
                  </div>
                </div>
              </router-link>
              <div class="event-details">
                <div class="event-details__battles">
                  <div
                    v-for="battle in event.battles"
                    :key="battle.id"
                    class="battle-item"
                  >
                    <span class="battle-item__title">
                      {{ battle.title }} {{ battle.crewSize }} vs {{ battle.crewSize }}
                    </span>
                    <span class="battle-item__rank">
                      {{ getRankText(battle.rank) }} <span :class="getRankIconClass(battle.rank)" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/style/mixins';

.studio-member-chip {
  display: flex;
  flex-direction: column;
  padding: 20px 0 4px;
  background-color: var(--color-grey-700);
  border: 1px solid var(--color-grey-700);
  border-radius: var(--border-radius-rounded);
  transition:
    background-color 0.3s,
    border-color 0.3s;

  &--expanded {
    background-color: var(--color-white);
    border-color: var(--color-grey-600);
  }

  .member-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 0 20px;

    &__left {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    &__profile-link {
      display: flex;
      gap: 8px;
      align-items: center;
      text-decoration: none;
      color: inherit;
    }

    &__image {
      width: 32px;
      height: 32px;
      border-radius: 999px;
    }

    &__right {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    &__icon {
      margin-left: 8px;
    }
  }

  .member-details__events {
    display: flex;
    flex-direction: column;
    gap: 40px;
    margin-top: 4px;
    padding: 20px 20px 16px;
    border-top: 1px solid var(--color-grey-600);
  }

  .event-item {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .event-header-link {
      text-decoration: none;
      color: inherit;
    }

    .event-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;

      &__left {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      &__image {
        width: 32px;
        height: 32px;
        border-radius: 4px;
      }

      &__right {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
      }

      &__icon {
        margin-left: 8px;
      }
    }

    .battle-item {
      display: flex;
      gap: 16px;
      justify-content: space-between;

      &__title,
      &__rank {
        @include font-body2;

        color: var(--color-grey-300);
      }

      &__rank {
        display: flex;
        gap: 4px;
        align-items: center;
      }
    }
  }

  .rank-icon {
    display: inline-block;
    width: 24px;
    height: 24px;
    background-size: contain;

    &.icon-gold {
      background-image: url('/img/ic_Trophy-gold.svg');
    }

    &.icon-silver {
      background-image: url('/img/ic_Trophy-silver.svg');
    }

    &.icon-bronze {
      background-image: url('/img/ic_Trophy-bronze.svg');
    }

    &.icon-compete {
      background-image: url('/img/ic_Compete-grey.svg');
    }
  }
}
</style>
